import { z } from 'zod';
import {
  CONTEST_STATUSES,
  DUPLICATE_STATUSES,
  PAGE_SIZES,
  READY_FOR_FOP,
  ROLES,
  SOURCE_KINDS,
} from './enums';
import { STAGE_COLORS, STAGE_KINDS, validateStages } from './stages';

// ───────────── Базові типи ─────────────

export const idSchema = z.uuid();
export const isoDateSchema = z.iso.date();

export const trimmed = (max: number) => z.string().trim().max(max);
export const requiredText = (max: number) => z.string().trim().min(1, 'Обов’язкове поле').max(max);
/** Порожній рядок → null, щоб форми з порожніми полями писали NULL. */
export const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v ? v : null));
export const optionalDate = isoDateSchema
  .or(z.literal(''))
  .nullish()
  .transform((v) => (v ? v : null));

export const passwordSchema = z.string().min(10, 'Мінімум 10 символів').max(200);

export const listParamsSchema = z.object({
  page: z.number().int().min(1).default(1),
  pageSize: z
    .number()
    .int()
    .refine((n) => (PAGE_SIZES as readonly number[]).includes(n))
    .default(25),
  sort: z.string().max(40).optional(),
  dir: z.enum(['asc', 'desc']).default('desc'),
  q: z.string().trim().max(200).optional(),
});
export type ListParams = z.infer<typeof listParamsSchema>;

/** Optimistic concurrency: клієнт передає `updatedAt`, який бачив. */
export const expectedVersionSchema = z.object({ expectedUpdatedAt: z.coerce.date().optional() });

// ───────────── Авторизація і користувачі ─────────────

export const loginInputSchema = z.object({
  email: z.email('Некоректний email').max(200),
  password: z.string().min(1, 'Введіть пароль').max(200),
});
export type LoginInput = z.infer<typeof loginInputSchema>;

export const userCreateSchema = z.object({
  email: z.email('Некоректний email').max(200),
  fullName: requiredText(200),
  role: z.enum(ROLES),
  /** Без пароля користувач отримує лист-запрошення і задає пароль сам. */
  password: passwordSchema.optional(),
});
export type UserCreateInput = z.infer<typeof userCreateSchema>;

export const inviteAcceptSchema = z.object({
  token: z.string().min(20).max(100),
  password: passwordSchema,
});
export type InviteAcceptInput = z.infer<typeof inviteAcceptSchema>;

export const userUpdateSchema = z.object({
  id: idSchema,
  fullName: requiredText(200).optional(),
  role: z.enum(ROLES).optional(),
  isActive: z.boolean().optional(),
  password: passwordSchema.optional(),
});

// ───────────── Довідники ─────────────

export const positionCreateSchema = z.object({ title: requiredText(200) });
export const positionUpdateSchema = z.object({
  id: idSchema,
  title: requiredText(200).optional(),
  isArchived: z.boolean().optional(),
});

export const sourceCreateSchema = z.object({ name: requiredText(200), kind: z.enum(SOURCE_KINDS) });
export const sourceUpdateSchema = sourceCreateSchema.partial().extend({ id: idSchema });

export const locationCreateSchema = z.object({ name: requiredText(200) });
export const locationUpdateSchema = locationCreateSchema.extend({ id: idSchema });

// ───────────── Конкурси ─────────────

/** Поля конкурсу без значень за замовчуванням: zod 4 підставляє default навіть у `.partial()`. */
const contestFieldsBase = z.object({
  positionId: idSchema,
  locationId: idSchema,
  /** Проєкт з реєстру; якщо задано, `projectCode` береться з нього. */
  projectId: idSchema.nullish().transform((v) => v ?? null),
  /** Штатна одиниця, на яку оголошено конкурс. */
  staffPositionId: idSchema.nullish().transform((v) => v ?? null),
  projectCode: trimmed(100),
  /** Порожньо → назва збирається автоматично з посади, міста і проєкту. */
  title: trimmed(300),
  announcedAt: isoDateSchema,
  protocol2Date: optionalDate,
  externalUrl: z
    .url('Некоректне посилання')
    .max(1000)
    .or(z.literal(''))
    .nullish()
    .transform((v) => (v ? v : null)),
  status: z.enum(CONTEST_STATUSES),
});

export const contestFieldsSchema = contestFieldsBase.extend({
  projectCode: trimmed(100).default(''),
  title: trimmed(300).default(''),
  status: z.enum(CONTEST_STATUSES).default('open'),
});
export type ContestFieldsInput = z.input<typeof contestFieldsSchema>;

export const contestCreateSchema = contestFieldsSchema;
export const contestUpdateSchema = contestFieldsBase
  .partial()
  .extend({ id: idSchema })
  .extend(expectedVersionSchema.shape);
export type ContestUpdateInput = z.input<typeof contestUpdateSchema>;

export const contestDescriptionSchema = z.object({
  id: idSchema,
  description: z.string().max(200_000),
  expectedUpdatedAt: z.coerce.date().optional(),
});

export const contestListSchema = listParamsSchema.extend({
  status: z.array(z.enum(CONTEST_STATUSES)).optional(),
  locationId: idSchema.optional(),
  positionId: idSchema.optional(),
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
});
export type ContestListInput = z.input<typeof contestListSchema>;

// ───────────── Кандидати ─────────────

export const candidateFieldsSchema = z.object({
  lastName: requiredText(100),
  firstName: requiredText(100),
  middleName: optionalText(100),
  phone: optionalText(100),
  email: z
    .email('Некоректний email')
    .max(200)
    .or(z.literal(''))
    .nullish()
    .transform((v) => (v ? v : null)),
  city: optionalText(100),
  notes: optionalText(5000),
});
export type CandidateFieldsInput = z.input<typeof candidateFieldsSchema>;

export const candidateUpdateSchema = candidateFieldsSchema
  .partial()
  .extend({ id: idSchema })
  .extend(expectedVersionSchema.shape);

export const candidateListSchema = listParamsSchema.extend({
  repeatOnly: z.boolean().optional(),
  city: z.string().trim().max(100).optional(),
});

export const duplicateCheckSchema = z.object({
  lastName: z.string().trim().max(100).default(''),
  firstName: z.string().trim().max(100).default(''),
  middleName: z.string().trim().max(100).nullish(),
  phone: z.string().trim().max(100).nullish(),
  email: z.string().trim().max(200).nullish(),
  resumeSha256: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .nullish(),
  excludeId: idSchema.optional(),
});
export type DuplicateCheckInput = z.input<typeof duplicateCheckSchema>;

// ───────────── Участь у конкурсі ─────────────

const applicationFieldsBase = z.object({
  sourceId: idSchema.nullish().transform((v) => v ?? null),
  appliedAt: optionalDate,
  education: optionalText(5000),
  experience: optionalText(5000),
  readyForFop: z.enum(READY_FOR_FOP),
  militaryDocs: optionalText(500),
  availableFrom: optionalText(200),
  recommendedPositionId: idSchema.nullish().transform((v) => v ?? null),
});

export const applicationFieldsSchema = applicationFieldsBase.extend({
  readyForFop: z.enum(READY_FOR_FOP).default('unknown'),
});
export type ApplicationFieldsInput = z.input<typeof applicationFieldsSchema>;

/** Швидке додавання в конкурс: новий кандидат або наявний (після перевірки дублів). */
export const applicationCreateSchema = z.object({
  contestId: idSchema,
  candidate: z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('existing'), id: idSchema }),
    z.object({
      mode: z.literal('new'),
      fields: candidateFieldsSchema,
      /** Схожі кандидати, яких користувач позначив як «Інша людина». */
      distinctFrom: z.array(idSchema).max(20).default([]),
    }),
  ]),
  fields: applicationFieldsSchema.prefault({}),
  resumeId: idSchema.nullish(),
});
export type ApplicationCreateInput = z.input<typeof applicationCreateSchema>;

export const applicationUpdateSchema = applicationFieldsBase
  .partial()
  .extend({
    id: idSchema,
    winnerLetterSentAt: optionalDate.optional(),
    rejectionLetterSentAt: optionalDate.optional(),
    startedAt: optionalDate.optional(),
    contractRef: optionalText(200).optional(),
    declinedReason: optionalText(1000).optional(),
    resumeId: idSchema.nullish(),
  })
  .extend(expectedVersionSchema.shape);
export type ApplicationUpdateInput = z.input<typeof applicationUpdateSchema>;

export const stageChangeSchema = z.object({
  id: idSchema,
  /** id етапу цього конкурсу. */
  to: idSchema,
  comment: optionalText(2000),
  startedAt: optionalDate,
  contractRef: optionalText(200),
  declinedReason: optionalText(1000),
  /** Позиція в колонці канбану після перетягування. */
  position: z.number().optional(),
  expectedUpdatedAt: z.coerce.date().optional(),
});
export type StageChangeInput = z.input<typeof stageChangeSchema>;

export const applicationListSchema = listParamsSchema.extend({
  contestId: idSchema,
  stageId: idSchema.optional(),
});
export type ApplicationListInput = z.input<typeof applicationListSchema>;

// ───────────── Етапи конкурсу ─────────────

/**
 * Набір етапів у порядку показу. `id` — наявний етап (перейменування, зміна порядку),
 * без `id` — новий. Етапи, яких немає в списку, видаляються (якщо на них нікого немає).
 */
export const stageListSchema = z
  .array(
    z.object({
      id: idSchema.optional(),
      name: requiredText(60),
      kind: z.enum(STAGE_KINDS).nullable(),
      color: z.enum(STAGE_COLORS),
    }),
  )
  .min(1)
  .max(30)
  .superRefine((stages, ctx) => {
    const error = validateStages(stages);
    if (error) ctx.addIssue({ code: 'custom', message: error });
  });
export type StageListInput = z.input<typeof stageListSchema>;

export const contestStagesSchema = z.object({
  contestId: idSchema,
  stages: stageListSchema,
});

export const userListSchema = listParamsSchema;

export const duplicateListSchema = listParamsSchema.extend({
  status: z.enum(DUPLICATE_STATUSES).default('open'),
});

export const applicationReorderSchema = z.object({ id: idSchema, position: z.number() });

// ───────────── Коментарі ─────────────

export const commentCreateSchema = z
  .object({
    applicationId: idSchema.optional(),
    candidateId: idSchema.optional(),
    body: requiredText(5000),
  })
  .refine((v) => Boolean(v.applicationId) !== Boolean(v.candidateId), {
    message: 'Потрібен або applicationId, або candidateId',
  });
export type CommentCreateInput = z.input<typeof commentCreateSchema>;

// ───────────── Дублікати ─────────────

export const mergeCandidatesSchema = z.object({
  /** Кандидат, який залишається. */
  keepId: idSchema,
  /** Кандидат, дані якого переносяться і який видаляється. */
  removeId: idSchema,
});
